import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  VpnKey as VpnKeyIcon,
  Shield as ShieldIcon,
  GroupWork as GroupWorkIcon,
} from "@mui/icons-material";
import PageLayout from "@/components/PageLayout";
import type { SsoProviderType } from "@/types";
import { useSsoProviders } from "./hooks/useSsoProviders";
import { usePortalRoles } from "./hooks/usePortalRoles";
import { usePortalDetails } from "./hooks/usePortalDetails";
import {
  type FormState,
  type SectionKey,
  emptyForm,
  providerFromRecord,
  buildRedirectUri,
  buildSamlAcsUrl,
  deriveSamlMetadataUrl,
  buildCreatePayload,
  buildUpdatePayload,
  computeSectionErrors,
  totalSectionErrors,
} from "./ssoProviderForm";

const SSO_TAB_INDEX = 6;
const FIELD_WIDTH = 280;
const LABEL_WIDTH = 220;

interface FieldRowProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  align?: "center" | "flex-start";
}

const FieldRow: React.FC<FieldRowProps> = ({
  label,
  hint,
  required,
  children,
  align = "center",
}) => (
  <Stack
    direction="row"
    alignItems={align}
    spacing={2}
    sx={{ minHeight: 40 }}
  >
    <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, pt: align === "flex-start" ? 1 : 0 }}>
      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.primary" }}>
        {label}
        {required && <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>*</Box>}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.3 }}>
          {hint}
        </Typography>
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const SsoProviderPage: React.FC = () => {
  const navigate = useNavigate();
  const { portalId, providerId } = useParams();
  const isEdit = Boolean(providerId);

  const { portal, isLoading: isLoadingPortal } = usePortalDetails(portalId);
  const {
    providers,
    createProvider,
    isCreating,
    updateProvider,
    isUpdating,
  } = useSsoProviders(portalId ?? "");
  const { roles: portalRoles } = usePortalRoles(portalId ?? "");
  const roleNameOptions = useMemo(
    () => portalRoles.filter((r) => r.is_active).map((r) => r.name),
    [portalRoles],
  );

  const editingProvider = useMemo(
    () => (isEdit ? providers.find((p) => p.id === providerId) ?? null : null),
    [isEdit, providerId, providers],
  );
  const hydrated = !isEdit || editingProvider !== null;

  const [form, setForm] = useState<FormState>(() =>
    editingProvider ? providerFromRecord(editingProvider) : emptyForm,
  );
  const [hydratedProviderId, setHydratedProviderId] = useState<string | null>(
    editingProvider ? editingProvider.id : null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState<
    "provider" | "credentials" | "access" | "roles"
  >("provider");

  if (
    isEdit &&
    editingProvider &&
    hydratedProviderId !== editingProvider.id
  ) {
    setForm(providerFromRecord(editingProvider));
    setHydratedProviderId(editingProvider.id);
  }

  const goBackToSsoTab = () => {
    navigate(`/dashboard/portals/${portalId}`, {
      state: { tab: SSO_TAB_INDEX },
    });
  };

  const isSaml = form.provider_type === "saml";

  const handleSubmit = () => {
    if (isEdit && providerId) {
      updateProvider(
        { id: providerId, payload: buildUpdatePayload(form) },
        {
          onSuccess: () => goBackToSsoTab(),
          onError: (err: unknown) => {
            const msg =
              (err as { message?: string })?.message ||
              "Failed to update provider";
            setFormError(msg);
          },
        },
      );
    } else {
      createProvider(buildCreatePayload(form), {
        onSuccess: () => goBackToSsoTab(),
        onError: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ||
            "Failed to create provider";
          setFormError(msg);
        },
      });
    }
  };

  const handleProviderTypeChange = (type: SsoProviderType) => {
    const displayNames: Record<SsoProviderType, string> = {
      google: "Sign in with Google",
      microsoft: "Sign in with Microsoft",
      oidc: "Sign in with SSO",
      saml: "Sign in with SSO",
    };
    setForm((prev) => ({
      ...prev,
      provider_type: type,
      display_name: prev.display_name || displayNames[type],
    }));
  };

  const sectionErrors = computeSectionErrors(form, isEdit);
  const isFormValid = totalSectionErrors(sectionErrors) === 0;

  const handleSaveClick = () => {
    if (!isFormValid) {
      const firstInvalid = (
        ["provider", "credentials", "access", "roles"] as SectionKey[]
      ).find((key) => sectionErrors[key] > 0);
      if (firstInvalid) setActiveSection(firstInvalid);
      return;
    }
    handleSubmit();
  };

  const saveSection = (section: SectionKey) => {
    if (sectionErrors[section] > 0) {
      setActiveSection(section);
      return;
    }
    handleSubmit();
  };

  if (isLoadingPortal || (isEdit && !hydrated)) {
    return (
      <PageLayout title={isEdit ? "Edit SSO Provider" : "Add SSO Provider"}>
        <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </PageLayout>
    );
  }

  const compactField = {
    size: "small" as const,
    fullWidth: true,
    sx: { maxWidth: FIELD_WIDTH },
  };

  const renderCopyBlock = (value: string) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 1.5,
        py: 0.75,
        bgcolor: "action.hover",
        borderRadius: 1,
        maxWidth: FIELD_WIDTH,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            wordBreak: "break-all",
          }}
        >
          {value}
        </Typography>
      </Box>
      <Tooltip title={copied ? "Copied!" : "Copy"}>
        <IconButton
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const redirectBlock = form.redirect_uri && renderCopyBlock(form.redirect_uri);

  // SAML SP metadata URL is derived from the stored ACS URL (.../acs -> .../metadata)
  // so the two always stay in sync, even if the ACS is hand-edited for local dev.
  const samlMetadataUrl = deriveSamlMetadataUrl(
    form.redirect_uri,
    portalId,
    form.provider_key,
  );

  return (
    <PageLayout
      title={isEdit ? "Edit SSO Provider" : "Add SSO Provider"}
      subtitle={portal?.name}
      backTo={`/dashboard/portals/${portalId}`}
    >
      <Box sx={{ maxWidth: 1040, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
            {isEdit ? "Edit SSO Provider" : "Add SSO Provider"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", mt: 0.25 }}>
            Configure single sign-on for this portal. Fields marked with
            <Box component="span" sx={{ color: "error.main", mx: 0.5 }}>*</Box>
            are required.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Stack direction="row" sx={{ minHeight: 480 }}>
            <Box
              sx={{
                width: 220,
                flexShrink: 0,
                borderRight: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                py: 1,
              }}
            >
              <List dense disablePadding>
                {[
                  { key: "provider", label: "PROVIDER", icon: <TuneIcon fontSize="small" /> },
                  { key: "credentials", label: "CREDENTIALS", icon: <VpnKeyIcon fontSize="small" /> },
                  { key: "access", label: "ACCESS", icon: <ShieldIcon fontSize="small" /> },
                  { key: "roles", label: "ROLE ASSIGNMENT", icon: <GroupWorkIcon fontSize="small" /> },
                ].map((item) => {
                  const errCount = sectionErrors[item.key as SectionKey] ?? 0;
                  return (
                  <ListItemButton
                    key={item.key}
                    selected={activeSection === item.key}
                    onClick={() =>
                      setActiveSection(item.key as typeof activeSection)
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{item.label}</span>
                          {errCount > 0 && (
                            <Badge
                              badgeContent={errCount}
                              color="error"
                              sx={{
                                "& .MuiBadge-badge": {
                                  position: "static",
                                  transform: "none",
                                  fontSize: "0.65rem",
                                  height: 16,
                                  minWidth: 16,
                                },
                              }}
                            />
                          )}
                        </Stack>
                      }
                      primaryTypographyProps={{
                        variant: "overline",
                        sx: {
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                        },
                      }}
                    />
                  </ListItemButton>
                  );
                })}
              </List>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, p: 3 }}>
              <Stack spacing={1.25}>
                {formError && (
                  <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 1 }}>
                    {formError}
                  </Alert>
                )}

                {activeSection === "provider" && (
                  <>
            {!isEdit && (
              <FieldRow label="Provider Type" required>
                <Select
                  value={form.provider_type}
                  size="small"
                  fullWidth
                  sx={{ maxWidth: FIELD_WIDTH }}
                  onChange={(e) =>
                    handleProviderTypeChange(e.target.value as SsoProviderType)
                  }
                >
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="microsoft">Microsoft</MenuItem>
                  <MenuItem value="oidc">OIDC (Okta, Auth0, Keycloak)</MenuItem>
                  <MenuItem value="saml">SAML 2.0 (Entra, ADFS, etc.)</MenuItem>
                </Select>
              </FieldRow>
            )}

            {!isEdit && (
              <FieldRow
                label="Provider Key"
                hint="URL-safe (e.g., 'google', 'okta-prod')"
                required
              >
                <TextField
                  {...compactField}
                  value={form.provider_key}
                  onChange={(e) => {
                    const key = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "-");
                    setForm((prev) => ({
                      ...prev,
                      provider_key: key,
                      // Both flows store a URL the IdP posts back to. OAuth: the
                      // OIDC callback. SAML: the ACS URL (registered at the IdP).
                      redirect_uri: !key
                        ? ""
                        : prev.provider_type === "saml"
                        ? buildSamlAcsUrl(portalId ?? "", key)
                        : buildRedirectUri(key),
                    }));
                  }}
                />
              </FieldRow>
            )}

            {!isSaml && redirectBlock && (
              <FieldRow label="Redirect URI" hint="Copy this to your IdP" align="flex-start">
                {redirectBlock}
              </FieldRow>
            )}

            {isSaml && (
              <FieldRow
                label="ACS URL"
                hint="Assertion Consumer Service — register this exact URL at your IdP"
                required
                align="flex-start"
              >
                <Stack spacing={0.5} sx={{ maxWidth: FIELD_WIDTH }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={form.redirect_uri}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, redirect_uri: e.target.value }))
                    }
                    sx={{ "& input": { fontFamily: "monospace", fontSize: "0.72rem" } }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                    The IdP POSTs the SAML assertion here. Must match exactly.
                  </Typography>
                </Stack>
              </FieldRow>
            )}

            {isSaml && samlMetadataUrl && (
              <FieldRow
                label="SP Metadata URL"
                hint="IdP can import this to auto-configure"
                align="flex-start"
              >
                {renderCopyBlock(samlMetadataUrl)}
              </FieldRow>
            )}

            <FieldRow
              label="Display Name"
              hint="Shown on login button"
              required
            >
              <TextField
                {...compactField}
                value={form.display_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, display_name: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow label="Target Audience" required>
              <Select
                value={form.target_audience}
                size="small"
                fullWidth
                sx={{ maxWidth: FIELD_WIDTH }}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    target_audience: e.target.value as "client" | "provider",
                  }))
                }
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="provider">Provider</MenuItem>
              </Select>
            </FieldRow>
                  </>
                )}

                {activeSection === "credentials" && !isSaml && (
                  <>
            <FieldRow label="Client ID" required>
              <TextField
                {...compactField}
                value={form.client_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, client_id: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow
              label="Client Secret"
              hint={isEdit ? "Leave blank to keep current" : undefined}
              required={!isEdit}
            >
              <TextField
                {...compactField}
                type="password"
                value={form.client_secret}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, client_secret: e.target.value }))
                }
              />
            </FieldRow>

            {form.provider_type === "microsoft" && (
              <FieldRow
                label="Tenant ID"
                hint="Azure AD tenant, 'common', or 'organizations'"
                required
              >
                <TextField
                  {...compactField}
                  value={form.tenant_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, tenant_id: e.target.value }))
                  }
                />
              </FieldRow>
            )}

            {form.provider_type === "oidc" && (
              <FieldRow
                label="Server Metadata URL"
                hint=".well-known/openid-configuration"
                required
              >
                <TextField
                  {...compactField}
                  value={form.server_metadata_url}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      server_metadata_url: e.target.value,
                    }))
                  }
                />
              </FieldRow>
            )}
                  </>
                )}

                {activeSection === "credentials" && isSaml && (
                  <>
            <FieldRow
              label="IdP Entity ID"
              hint="EntityID from the IdP metadata"
              required
            >
              <TextField
                {...compactField}
                value={form.idp_entity_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, idp_entity_id: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow
              label="IdP SSO URL"
              hint="SingleSignOnService (HTTP-Redirect) endpoint"
              required
            >
              <TextField
                {...compactField}
                value={form.idp_sso_url}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, idp_sso_url: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow
              label="IdP Signing Certificate"
              hint="X.509 PEM body from the IdP metadata"
              required
              align="flex-start"
            >
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={4}
                value={form.idp_x509_cert}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, idp_x509_cert: e.target.value }))
                }
                sx={{ "& textarea": { fontFamily: "monospace", fontSize: "0.72rem" } }}
              />
            </FieldRow>

            <FieldRow
              label="Email Attribute"
              hint="Assertion attribute carrying email (else NameID is used)"
            >
              <TextField
                {...compactField}
                value={form.attr_email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, attr_email: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow label="Display Name Attribute" hint="Optional full-name attribute">
              <TextField
                {...compactField}
                value={form.attr_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, attr_name: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow label="First / Last Name Attributes" hint="Optional; used if no full name" align="flex-start">
              <Stack direction="row" spacing={1} sx={{ maxWidth: FIELD_WIDTH }}>
                <TextField
                  size="small"
                  label="First"
                  value={form.attr_first_name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, attr_first_name: e.target.value }))
                  }
                />
                <TextField
                  size="small"
                  label="Last"
                  value={form.attr_last_name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, attr_last_name: e.target.value }))
                  }
                />
              </Stack>
            </FieldRow>

            <FieldRow
              label="Require Signed Assertions"
              hint="Recommended. Reject unsigned assertions."
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={form.want_assertions_signed}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        want_assertions_signed: e.target.checked,
                      }))
                    }
                  />
                }
                label=""
              />
            </FieldRow>
                  </>
                )}

                {activeSection === "access" && (
                  <>
            {!isSaml && (
              <FieldRow label="Scopes" hint="Space-separated OAuth scopes">
                <TextField
                  {...compactField}
                  value={form.scopes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, scopes: e.target.value }))
                  }
                />
              </FieldRow>
            )}

            <FieldRow
              label="Allowed Email Domains"
              hint="Comma-separated. Empty = allow all."
            >
              <TextField
                {...compactField}
                value={form.allowed_email_domains}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    allowed_email_domains: e.target.value,
                  }))
                }
              />
            </FieldRow>

            <FieldRow label="Enabled">
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={form.is_enabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label=""
              />
            </FieldRow>
                  </>
                )}

                {activeSection === "roles" && (
                  <>
            <FieldRow
              label={isSaml ? "Groups Attribute" : "Role Claim Field"}
              hint={
                isSaml
                  ? "SAML attribute carrying group/role membership"
                  : "IdP token field (e.g., 'groups', 'roles')"
              }
            >
              <TextField
                {...compactField}
                value={form.role_claim}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, role_claim: e.target.value }))
                }
              />
            </FieldRow>

            <FieldRow
              label="Claim Mappings"
              hint="Map IdP claim values to portal roles"
              align="flex-start"
            >
              <Stack spacing={1}>
                {form.role_mappings.map((row, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <TextField
                      label="Claim Value"
                      value={row.claimValue}
                      onChange={(e) => {
                        const updated = [...form.role_mappings];
                        updated[index] = { ...row, claimValue: e.target.value };
                        setForm((prev) => ({ ...prev, role_mappings: updated }));
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Autocomplete
                      multiple
                      size="small"
                      options={roleNameOptions}
                      value={row.roleNames}
                      onChange={(_, newVal) => {
                        const updated = [...form.role_mappings];
                        updated[index] = { ...row, roleNames: newVal };
                        setForm((prev) => ({ ...prev, role_mappings: updated }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Portal Roles" />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, i) => (
                          <Chip
                            {...getTagProps({ index: i })}
                            key={option}
                            label={option}
                            size="small"
                          />
                        ))
                      }
                      sx={{ flex: 1.5 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          role_mappings: prev.role_mappings.filter(
                            (_, i) => i !== index,
                          ),
                        }));
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      role_mappings: [
                        ...prev.role_mappings,
                        { claimValue: "", roleNames: [] },
                      ],
                    }))
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  Add Mapping
                </Button>
              </Stack>
            </FieldRow>

            <FieldRow
              label="Default Roles"
              hint="Fallback when no claim mapping matches"
              align="flex-start"
            >
              <Autocomplete
                multiple
                size="small"
                options={roleNameOptions}
                value={form.default_roles}
                onChange={(_, newVal) =>
                  setForm((prev) => ({ ...prev, default_roles: newVal }))
                }
                renderInput={(params) => <TextField {...params} />}
                renderTags={(value, getTagProps) =>
                  value.map((option, i) => (
                    <Chip
                      {...getTagProps({ index: i })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </FieldRow>
                  </>
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={1.5}
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
              >
                {!isEdit && !isFormValid && (
                  <Typography variant="caption" color="text.secondary">
                    Complete required fields to enable create.
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={goBackToSsoTab}
                  disabled={isCreating || isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={
                    isEdit
                      ? () => saveSection(activeSection)
                      : handleSaveClick
                  }
                  disabled={
                    isCreating ||
                    isUpdating ||
                    (!isEdit && !isFormValid) ||
                    (isEdit && sectionErrors[activeSection] > 0)
                  }
                  startIcon={
                    isCreating || isUpdating ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                >
                  {isEdit
                    ? `Save ${
                        activeSection === "roles"
                          ? "Role Assignment"
                          : activeSection.charAt(0).toUpperCase() +
                            activeSection.slice(1)
                      }`
                    : "Create"}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default SsoProviderPage;
